/** @format */

import { create } from "zustand";
import type { User } from "./type";
import { UserRole } from "./type";
import { persist } from "zustand/middleware";
import * as Sentry from "@sentry/react";

async function hashAnonymously(id: string): Promise<string> {
  const data = new TextEncoder().encode(id);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(hashBuffer)).map(b => b.toString(16).padStart(2, '0')).join('');
}

type AuthState = {
  user: User | null;
  isAuthenticated: boolean;
  token: string | null;
  isLoading: boolean;
  expiresAt: number | null;
  staySignedIn: boolean;
  selectedTenantId: string | null;
  login: (user: User, token: string, staySignedIn?: boolean) => Promise<void>;
  logout: () => void;
  setLoading: (loading: boolean) => void;
  setUser: (user: User | null) => void;
  setSelectedTenantId: (id: string | null) => void;
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      isAuthenticated: false,
      token: null,
      isLoading: false,
      expiresAt: null,
      staySignedIn: false,
      selectedTenantId: null,

      login: async (user: User, token: string, staySignedIn = false) => {
        // Validate user role - only allow known admin roles
        const validRoles = Object.values(UserRole) as string[];
        if (!validRoles.includes(user.roleName)) {
          throw new Error('Access denied. Admin privileges required.');
        }

        const expiresAt = staySignedIn ? Date.now() + 4 * 60 * 60 * 1000 : null;

        set({
          user,
          token,
          isAuthenticated: true,
          isLoading: false,
          staySignedIn,
          expiresAt,
          // Merchant-scoped users have their merchantId in the JWT; osprey admins select manually
          selectedTenantId: user.merchantId ?? null,
        });

        hashAnonymously(user.id).then((id) => Sentry.setUser({ id }));
      },

      logout: () => {
        Sentry.setUser(null);
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
          staySignedIn: false,
          expiresAt: null,
          selectedTenantId: null,
        });
      },

      setSelectedTenantId: (id: string | null) => {
        set({ selectedTenantId: id });
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },

      setUser: (user: User | null) => {
        set({ user });
      },
    }),
    {
      name: "auth-storage", // Key name in browser's local storage
      storage: {
        getItem: (name: string) => {
          // Prevent SSR storage access
          if (typeof window === "undefined") return null;
          
          let value = localStorage.getItem(name);
          if (!value) {
            value = sessionStorage.getItem(name);
          }
          
          if (value) {
            try {
              const parsed = JSON.parse(value);
              if (parsed?.state?.expiresAt) {
                if (Date.now() > parsed.state.expiresAt) {
                  localStorage.removeItem(name);
                  sessionStorage.removeItem(name);
                  return null;
                }
              }
              return parsed;
            } catch {
              return null;
            }
          }
          return null;
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        setItem: (name: string, value: any) => {
          if (typeof window === "undefined") return;
          const staySignedIn = value?.state?.staySignedIn;
          if (staySignedIn) {
            localStorage.setItem(name, JSON.stringify(value));
            sessionStorage.removeItem(name);
          } else {
            sessionStorage.setItem(name, JSON.stringify(value));
            localStorage.removeItem(name);
          }
        },
        removeItem: (name: string) => {
          if (typeof window === "undefined") return;
          localStorage.removeItem(name);
          sessionStorage.removeItem(name);
        },
      },
      // Only save these fields to storage - skip loading states to avoid bugs
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
        staySignedIn: state.staySignedIn,
        expiresAt: state.expiresAt,
        selectedTenantId: state.selectedTenantId,
      }),
      // Force-logout on rehydration if auth state is inconsistent or role is stale
      onRehydrateStorage: () => (state) => {
        if (!state?.isAuthenticated) return;
        const validRoles = Object.values(UserRole) as string[];
        const roleName = state.user?.roleName;
        if (!roleName || !validRoles.includes(roleName)) {
          state.logout();
        }
      },
  }))
